<template>
  <div class="w-full" ref="containerRef">
    <Header v-if="!isIframe" :class="{ 'header--hidden': !headerLogicReady }" />

    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { iframeAuthRef } from '~/authBridge'
// Import hlavičky a reaktivní proměnné
import Header from '~/components/Default/Header.vue';

const headerLogicReady = ref<boolean>(false)
const isIframe = ref<boolean>(false)
const iframeStore = useIframeStore()

const DEBUG = true
const allowedParentOrigins = [
  'https://www.blesk.cz',
  'https://bleskovehry.blesk.cz'
]

let isSafariOrITPBrowser = false
let isITPRiskIframe = false
if (import.meta.client) {
   isSafariOrITPBrowser = /Safari|iPhone|iPod|iPad/.test(navigator.userAgent) && !/Chrome|CriOS/.test(navigator.userAgent);
   isITPRiskIframe = isSafariOrITPBrowser;
  if (DEBUG) console.log('[Iframe Auth Sync] ITP Risk Detection:', isITPRiskIframe ? '🍎 Safari/ITP DETECTED' : 'Standard Browser');
}


const config = useRuntimeConfig()
const userStore = useUserStore()

// Kontrola přihlášení z userStore
function isLoggedIn(): boolean {
  return userStore.isLoggedIn || false
}

// Provede přihlášení - redirect na Sentinel SSO (přes Nitro server route)
function performLogin() {
  log('Performing login - redirecting to Sentinel SSO')

  const ssoLoginPath = String(config.public.ssoLoginPath || '/auth/sso/login')

  // Sentinel sends `frame-ancestors 'self'`, so it refuses to render inside any
  // iframe - break the whole tab out instead of navigating just the iframe.
  // Parent's URL isn't readable (cross-origin), so we send no loginCallbackUrl and
  // let the callback route fall back to NUXT_PUBLIC_AUTO_LOGIN_CALLBACK_URL.
  if (import.meta.client && window.self !== window.top) {
    log('Inside iframe - breaking out to top window')
    window.top!.location.href = `${window.location.origin}${ssoLoginPath}`
    return
  }

  // Aktuální URL použijeme jako návratovou po přihlášení
  const callbackUrl = window.location.href
  window.location.href = `${ssoLoginPath}?loginCallbackUrl=${encodeURIComponent(callbackUrl)}`
}

function log(message: string, data?: any) {
  if (DEBUG) console.log('[Iframe Auth Sync] ' + message, data ?? '')
}

function isParentOriginAllowed(origin: string) {
  return allowedParentOrigins.some(o => origin.startsWith(o))
}

// --- Template ref ---
const containerRef = ref<HTMLElement | null>(null)
let debounce: number | null = null

// --- PostMessage helpers ---
const sendHeightToParent = (height: number) => {
  window.parent.postMessage({ type: 'setHeight', height }, '*')
}

const sendLoginToParent = (requiresReload = true) => {
  log('Notifying parent about login')
  window.parent.postMessage({
    type: 'AUTH_LOGIN',
    requiresReload,
    source: 'iframe',
    timestamp: new Date().getTime()
  }, '*')
}

const sendLogoutToParent = (requiresReload = true) => {
  log('Notifying parent about logout')
  window.parent.postMessage({
    type: 'AUTH_LOGOUT',
    requiresReload,
    source: 'iframe',
    timestamp: new Date().getTime()
  }, '*')
}

const requestParentAuthStatus = () => {
  log('Requesting parent auth status')
  window.parent.postMessage({
    type: 'AUTH_STATUS_REQUEST',
    timestamp: new Date().getTime()
  }, '*')
}

const sendIframeReady = () => {
  log('Sending iframe ready signal')
  window.parent.postMessage({
    type: 'IFRAME_READY',
    timestamp: new Date().getTime()
  }, '*')
}

const sendAuthStateCheck = () => {
  const loggedIn = isLoggedIn()
  log('Sending auth state check', { isLoggedIn: loggedIn })
  window.parent.postMessage({
    type: 'AUTH_STATE_CHECK',
    isLoggedIn: loggedIn,
    timestamp: new Date().getTime()
  }, '*')
}

// --- Sync with parent state ---
async function syncWithParentAuthState(parentIsLoggedIn: boolean) {
  const iframeIsLoggedIn = isLoggedIn()

  log('Syncing with parent auth state', {
    parent: parentIsLoggedIn,
    iframe: iframeIsLoggedIn
  })

  // Sentinel login je nezávislý na přihlášení do portálu - iframe přihlášený
  // a parent nepřihlášený (nebo naopak, mimo tenhle jeden case) je očekávaný
  // stav, ne chyba k opravení. Iframe se tedy z parenta neodhlašuje.
  if (parentIsLoggedIn && !iframeIsLoggedIn) {
    log('Parent is logged in, iframe is not → performing login')
    performLogin()
  } else {
    log('No sync action needed', { parent: parentIsLoggedIn, iframe: iframeIsLoggedIn })
  }
}

// --- Message handler ---
const handleParentMessage = (event: MessageEvent) => {
  const data = event.data
  if (!data?.type) return

  // Pro AUTH zprávy kontrolujeme origin
  if (data.type.startsWith('AUTH_') && !isParentOriginAllowed(event.origin)) {
    log('Ignoring auth message from unknown origin: ' + event.origin)
    return
  }

  log('Received message: ' + data.type, data)

  switch (data.type) {
    case 'AUTH_STATUS_RESPONSE':
      // Parent nám poslal svůj auth stav → synchronizujeme
      if (typeof data.isLoggedIn === 'boolean') {
        syncWithParentAuthState(data.isLoggedIn)
      }
      break

    case 'setParentInfo':
      if (data.data) {
        log('Received parent info', data.data)
        // Uložení dat do Pinia Store
        iframeStore.top = data.data.top
        iframeStore.iframeTop = data.data.iframeTop
        iframeStore.screenHeight = data.data.screenHeight
        iframeStore.iframeHeight = data.data.iframeHeight
      }
      break
  }
}


// Zavolat tyto metody když uživatel provede login/logout v iframu
/**
 * Zavolat tuto funkci když se uživatel přihlásí v iframu
 * @param requiresReload - zda parent musí udělat reload (default: true)
 */
const notifyParentLogin = (requiresReload = true) => {
  log('User logged in, notifying parent')
  sendLoginToParent(requiresReload)
}

/**
 * Zavolat tuto funkci když se uživatel odhlásí v iframu
 * @param requiresReload - zda parent musí udělat reload (default: true)
 */
const notifyParentLogout = (requiresReload = true) => {
  log('User logged out, notifying parent')
  sendLogoutToParent(requiresReload)
}

/**
 * Vyžádá aktuální auth stav od parenta
 */

const syncWithParent = () => {
  log('Manually requesting sync with parent')
  requestParentAuthStatus()
}

// --- Lifecycle ---
onMounted(() => {
  log('Vue app mounted, initializing auth sync', {
    isLoggedIn: userStore.isLoggedIn
  })

  // --- KONTROLA IFRAME REŽIMU (přidáno) ---
  if (window.self !== window.top) {
    isIframe.value = true;
    log('Running inside an iframe.');
  } else {
    isIframe.value = false;
    log('Running in standalone mode.');
  }
  // -----------------------------------------

  headerLogicReady.value = true;

  // Resize observer pro dynamickou výšku
  useResizeObserver(containerRef, (entries) => {
    const entry = entries[0]
    if (!entry) return
    if (debounce) window.clearTimeout(debounce)
    debounce = window.setTimeout(() => {
      // Odesíláme výšku POUZE, pokud jsme v iframu (výška standalone aplikace nás nezajímá)
      if (isIframe.value) {
        sendHeightToParent(entry.contentRect.height)
      }
    }, 100) as unknown as number
  })

  // Přidáme message listener POUZE, pokud jsme v iframu
  if (isIframe.value) {
      window.addEventListener('message', handleParentMessage, false)

      // Inicializace komunikace s parentem
      sendIframeReady()
      sendAuthStateCheck()
  }
})

onUnmounted(() => {
  if (isIframe.value) {
    window.removeEventListener('message', handleParentMessage, false)
  }
  if (debounce) {
    window.clearTimeout(debounce)
    debounce = null
  }
})

// --- Expose methods pro použití z parent komponent ---
defineExpose({
  notifyParentLogin,
  notifyParentLogout,
  syncWithParent,
  isLoggedIn
})

onMounted(() => {
  // zpřístupníme funkce celému projektu
  iframeAuthRef.value = {
    notifyParentLogin,
    notifyParentLogout,
    syncWithParent,
    isLoggedIn
  }
})

onUnmounted(() => {
  iframeAuthRef.value = null
})

</script>
